import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
 
import { UpdateModule } from "../../../redux/services/moduleService/module.Services";
 
import { successToast, errorToast } from "../../../Utils/toast";
 
import {
  closeProductModal,
  refreshProductList,
} from "../../../redux/features/productModalSlice";
 
import Swal from "sweetalert2";
 
const MAX_TODO = 5;
 
function EditModule({ data, onSuccess }) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
 
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: true,
    todo_list: [""],
  });
 
  // ✅ LOAD DATA (including todo_list + description)
  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        description: data.description || "",
        status: data.status === 1 || data.status === true,
        todo_list:
          data.todo_list && data.todo_list.length > 0
            ? data.todo_list
            : [""],
      });
    }
  }, [data]);
 
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "status" ? value === "Active" : value,
    }));
 
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };
 
  // ✅ Todo list change
  const handleTodoChange = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.todo_list];
      updated[index] = value;
      return { ...prev, todo_list: updated };
    });
 
    if (errors.todo_list) {
      setErrors((prev) => ({ ...prev, todo_list: null }));
    }
  };
 
  // ✅ Add todo
  const addTodoItem = () => {
    if (formData.todo_list.length >= MAX_TODO) {
      Swal.fire(
        "Limit Reached",
        `You can add a maximum of ${MAX_TODO} todo items.`,
        "warning"
      );
      return;
    }
    setFormData((prev) => ({
      ...prev,
      todo_list: [...prev.todo_list, ""],
    }));
  };
 
  // ✅ Remove todo
  const removeTodoItem = (index) => {
    setFormData((prev) => {
      const updated = prev.todo_list.filter((_, i) => i !== index);
      return { ...prev, todo_list: updated.length ? updated : [""] };
    });
  };
 
  // ✅ Validation
  const validateForm = () => {
    const newErrors = {};
 
    if (!formData.name.trim()) {
      newErrors.name = ["Name is required"];
    }
 
    const cleanedTodos = formData.todo_list
      .map((t) => t.trim())
      .filter((t) => t !== "");
 
    if (cleanedTodos.length > MAX_TODO) {
      newErrors.todo_list = [`Maximum ${MAX_TODO} todo items allowed`];
    }
 
    setErrors(newErrors);
    return newErrors;
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
 
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setTimeout(() => {
        const firstErrorField = document.querySelector(".is-invalid");
        if (firstErrorField) {
          firstErrorField.focus();
          firstErrorField.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
      return;
    }
 
    try {
      setIsLoading(true);
 
      const cleanedTodoList = formData.todo_list
        .map((t) => t.trim())
        .filter((t) => t !== "")
        .slice(0, MAX_TODO);
 
      const payload = {
        id: data?.id,
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: formData.status ? 1 : 0,
        todo_list: cleanedTodoList,
      };
 
      const res = await UpdateModule(payload);
 
      if (res?.success) {
        successToast(res?.message || "Module updated successfully!");
        if (onSuccess) await onSuccess();
        dispatch(refreshProductList());
        dispatch(closeProductModal());
      } else {
        errorToast(res?.message || "Failed to update module");
      }
    } catch (err) {
      console.log(err);
 
      if (err?.response?.status === 422) {
        setErrors(err.response.data.errors || {});
        errorToast("Please check the form fields");
      } else {
        errorToast(
          err?.response?.data?.message || "Error updating module"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };
 
  return (
    <div className="modal-form-wrapper">
      <form onSubmit={handleSubmit} className="row">
        {/* ---------- Name ---------- */}
        <div className="col-md-12 mb-3">
          <label>
            Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`form-control ${errors?.name ? "is-invalid" : ""}`}
            placeholder="Enter module name"
          />
          {errors?.name && (
            <div className="invalid-feedback d-block">{errors.name[0]}</div>
          )}
        </div>
 
        {/* ---------- Description ---------- */}
        <div className="col-md-12 mb-3">
          <label>Description</label>
          <textarea
            name="description"
            rows="3"
            value={formData.description}
            onChange={handleChange}
            className={`form-control ${
              errors?.description ? "is-invalid" : ""
            }`}
            placeholder="Module description"
          />
          {errors?.description && (
            <div className="invalid-feedback d-block">
              {errors.description[0]}
            </div>
          )}
        </div>
 
        {/* ---------- Todo List ---------- */}
        <div className="col-md-12 mb-3">
          <label>
            Todo List{" "}
            <small className="text-muted">
              ({formData.todo_list.length}/{MAX_TODO})
            </small>
          </label>
 
          {formData.todo_list.map((todo, index) => (
            <div className="input-group mb-2" key={index}>
              <input
                type="text"
                className={`form-control ${
                  errors?.todo_list ? "is-invalid" : ""
                }`}
                placeholder={`Task ${index + 1}`}
                value={todo}
                onChange={(e) => handleTodoChange(index, e.target.value)}
                maxLength={255}
              />
 
              {index === 0 ? (
                // ✅ First row → + Add button
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={addTodoItem}
                  disabled={formData.todo_list.length >= MAX_TODO}
                >
                  + Add
                </button>
              ) : (
                // ✅ Baaki rows → ✕ Close button
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => removeTodoItem(index)}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
 
          {errors?.todo_list && (
            <div className="invalid-feedback d-block">
              {errors.todo_list[0]}
            </div>
          )}
        </div>
 
        {/* ---------- Status ---------- */}
        <div className="col-md-12 mb-3">
          <label>Status</label>
          <select
            name="status"
            value={formData.status ? "Active" : "Inactive"}
            onChange={handleChange}
            className="form-control"
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
 
        {/* ---------- Submit ---------- */}
        <div className="col-md-12 text-end">
          <button
            type="submit"
            disabled={isLoading}
            className="add-btn"
          >
            {isLoading ? "Updating..." : "Update Module"}
          </button>
        </div>
      </form>
    </div>
  );
}
 
export default EditModule;